import { useEffect, useState } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { ArrowsClockwiseIcon, ExportIcon } from "@phosphor-icons/react";

function exportCSV(rows, filename) {
  const header = Object.keys(rows[0] || {}).join(";");
  const body = rows.map((r) => Object.values(r).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Financial() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [monthly, setMonthly] = useState(null);
  const [start, setStart] = useState(`${year}-01-01`);
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [cash, setCash] = useState(null);

  const loadMonthly = async () => {
    const { data } = await api.get("/financial/monthly", { params: { year } });
    setMonthly(data);
  };

  const loadCashflow = async () => {
    const { data } = await api.get("/financial/cashflow", { params: { start, end } });
    setCash(data);
  };

  useEffect(() => { loadMonthly(); /* eslint-disable-next-line */ }, [year]);
  useEffect(() => { loadCashflow(); /* eslint-disable-next-line */ }, [start, end]);

  const chartData = (monthly?.months || []).map((m) => ({
    mes: m.month.slice(5),
    Entradas: m.income,
    Saídas: m.outflow,
    Líquido: m.net,
  }));

  return (
    <div data-testid="financial-page">
      <PageHeader
        title="Financeiro"
        subtitle="Fluxo de caixa, relatórios mensais e exportação."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Entradas (período)</div>
          <div className="font-display text-2xl font-bold text-emerald-400 mt-1">{brl(cash?.income || 0)}</div>
        </div>
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Saídas (período)</div>
          <div className="font-display text-2xl font-bold text-rose-400 mt-1">{brl(cash?.outflow || 0)}</div>
        </div>
        <div className="glass rounded-xl p-5">
          <div className="text-xs uppercase tracking-wider text-slate-400">Líquido (período)</div>
          <div className={`font-display text-2xl font-bold mt-1 ${(cash?.net || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{brl(cash?.net || 0)}</div>
        </div>
      </div>

      <div className="glass rounded-xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">De</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="cf-start" className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Até</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="cf-end" className="bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
          </div>
          <button onClick={loadCashflow} data-testid="cf-refresh" className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={14} /> Atualizar
          </button>
          <button
            onClick={() => exportCSV(cash?.entries || [], `fluxo_${start}_${end}.csv`)}
            disabled={!cash?.entries?.length}
            className="px-3 py-2 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-300 text-sm flex items-center gap-2 border border-emerald-500/20 disabled:opacity-50"
            data-testid="cf-export"
          >
            <ExportIcon size={14} /> Exportar CSV
          </button>
        </div>

        <div className="overflow-x-auto max-h-[400px]">
          <table className="w-full text-sm" data-testid="cashflow-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5 sticky top-0 bg-slate-950">
              <tr>
                <th className="py-2 pr-4">Tipo</th>
                <th className="py-2 pr-4">Data</th>
                <th className="py-2 pr-4">Descrição</th>
                <th className="py-2 text-right">Valor</th>
              </tr>
            </thead>
            <tbody>
              {(cash?.entries || []).map((e, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2 pr-4">
                    <span className={`text-xs px-2 py-0.5 rounded-md border ${e.type === "entrada" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20" : "bg-rose-500/10 text-rose-300 border-rose-500/20"}`}>
                      {e.type}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-slate-400">{fmtDate(e.date)}</td>
                  <td className="py-2 pr-4">{e.description}</td>
                  <td className={`py-2 text-right font-medium ${e.type === "entrada" ? "text-emerald-400" : "text-rose-400"}`}>
                    {e.type === "entrada" ? "+" : "−"} {brl(e.value)}
                  </td>
                </tr>
              ))}
              {(!cash?.entries || cash.entries.length === 0) && (
                <tr><td colSpan={4} className="py-12 text-center text-slate-500">Sem movimentações no período.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="glass rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
          <div>
            <h3 className="font-display text-lg font-semibold">Relatório anual</h3>
            <p className="text-xs text-slate-500">Comparativo mensal de entradas, saídas e líquido</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="report-year" className="w-24 bg-black/20 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
            <button
              onClick={() => exportCSV(monthly?.months || [], `anual_${year}.csv`)}
              disabled={!monthly?.months?.length}
              data-testid="report-export"
              className="px-3 py-2 rounded-lg bg-emerald-600/15 hover:bg-emerald-600/25 text-emerald-300 text-sm flex items-center gap-2 border border-emerald-500/20"
            >
              <ExportIcon size={14} /> Exportar CSV
            </button>
          </div>
        </div>

        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="mes" stroke="#64748b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v) => `R$${v}`} />
              <Tooltip contentStyle={{ background: "rgba(2,6,23,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.5rem" }} formatter={(v) => brl(v)} />
              <Legend />
              <Bar dataKey="Entradas" fill="#10b981" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Saídas" fill="#f43f5e" radius={[6, 6, 0, 0]} />
              <Bar dataKey="Líquido" fill="#3b82f6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
